import { useRef } from 'react';
import { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as Sentry from '@sentry/react';

import { InterviewType } from '@common/ts/enums';
import { getErrorMessage } from '@common/utils/getErrorDetails';

import { ERROR_MESSAGES } from '@common/constants';
import useChatStore from '../store';
import { iframeLocalStorage } from '../utils/iframeLocalStorage';
import useChatSettingsStore from '../ChatSettingsStore';
import { NetworkType, TimeoutProfile } from '../enums';
import { AbortTimeoutConfig } from '../types';

interface TSendMessageProps {
  file?: Blob | null;
  text: string;
  onError: (error: Error) => void;
}

const useServerMessageHandler = () => {
  const {
    setSeen,
    setReceived,
    setInputsDisabled,
    setStatusOnLastMessage,
    updateLastMessage,
    refreshChatHistory,
    refreshTimeout,
    refreshMessage,
  } = useChatStore();
  const { chatSettingsData } = useChatSettingsStore();
  const isTextChat = chatSettingsData?.includes(InterviewType.TEXT);

  const messageHandler = async (messageData: TResponseHandlerProps, timeout: NodeJS.Timeout) => {
    const {
      data: { message },
      event,
    } = messageData;

    clearTimeout(timeout);
    if (event === 'SEEN') {
      return setSeen();
    }
    if (event === 'STATUS') {
      return setStatusOnLastMessage(message);
    }
    if (event === 'MESSAGE') {
      updateLastMessage(message);
      iframeLocalStorage.removeItem('last_user_message');
      return setInputsDisabled(false);
    }
    if (event === 'MESSAGE_RECEIVED') {
      return setReceived();
    }
    if (event === 'TIMEOUT') {
      await refreshChatHistory?.();
      toast.error(
        "Oops! Looks like we're having trouble processing your message. Please try resending it.",
        {
          autoClose: 10000,
        },
      );
      return refreshTimeout();
    }
    if (event === 'NO_SPEECH_DETECTED') {
      const noSpeechDetectedMessage = isTextChat
        ? 'Please type your answers using the keyboard button.'
        : 'Please check your browser permissions and try again.';

      toast.error(`It seems there is a problem with your microphone. ${noSpeechDetectedMessage}`, {
        autoClose: 10000,
      });

      const messages = [...useChatStore.getState().data.messages];

      if (messages.length > 0) {
        // And also remove the assistant loading message if it exists
        if (messages.length >= 2 && messages[messages.length - 2].role === 'user') {
          // find the last user message and remove, and set new field called hasSpeech
          messages[messages.length - 2].hasSpeech = false;
        }

        if (
          messages.length > 0 &&
          messages[messages.length - 1].role === 'assistant' &&
          messages[messages.length - 1].loading
        ) {
          messages.pop();
        }

        useChatStore.setState({
          data: {
            ...useChatStore.getState().data,
            messages,
          },
        });
      }

      setInputsDisabled(false);
      // eslint-disable-next-line consistent-return
      return;
    }
    if (event === 'ERROR') {
      return refreshMessage(message);
    }
    return null;
  };

  return messageHandler;
};

function parse(source: AllowSharedBufferSource): Record<string, unknown> | undefined {
  const textDecoder = new TextDecoder('utf-8');
  const decoded = textDecoder.decode(source);
  if (decoded === '\n') {
    return undefined;
  }
  const lines = decoded.split('\n');
  const message: Record<string, unknown> = {};

  lines.forEach((line) => {
    const index = line.indexOf(':');
    const field = line.substring(0, index);
    const value = line.substring(index + 1);
    if (field) {
      message[field] = field === 'data' ? JSON.parse(value) : value;
    }
  });

  return message;
}

const useSendMessage = () => {
  const messageHandler = useServerMessageHandler();
  const { refreshChatHistory, setInputsDisabled } = useChatStore((state) => state);
  const hasReceivedFirstResponse = useRef(false);

  const TIMEOUTS = {
    main: {
      [TimeoutProfile.MobileLarge]: 60000,
      [TimeoutProfile.Mobile]: 40000,
      [TimeoutProfile.Desktop]: 30000,
    },
    initial: {
      [TimeoutProfile.MobileLarge]: 30000,
      [TimeoutProfile.Mobile]: 20000,
      [TimeoutProfile.Desktop]: 15000,
    },
  };

  const getTimeoutProfile = (isMobile: boolean, hasLargeFile: boolean): TimeoutProfile => {
    if (isMobile && hasLargeFile) return TimeoutProfile.MobileLarge;
    if (isMobile) return TimeoutProfile.Mobile;
    return TimeoutProfile.Desktop;
  };

  const calculateTimeoutDurations = (isMobile: boolean, hasLargeFile: boolean) => {
    const profile = getTimeoutProfile(isMobile, hasLargeFile);
    return {
      mainTimeoutDuration: TIMEOUTS.main[profile],
      initialTimeoutDuration: TIMEOUTS.initial[profile],
    };
  };

  // increased base timeout to 60 seconds due to prematured abort action with large files
  // TODO: investigate a better way to handle large files without increasing the timeout
  const BASE_ABORT_TIMEOUT = 60000;
  const FileSizeAdjustments = {
    Mobile: {
      perMB: 10000,
      max: 140000,
    },
    Desktop: {
      perMB: 5000,
      max: 80000,
    },
  };

  const NetworkAdjustments: Record<NetworkType, number> = {
    [NetworkType._2G]: 60000,
    [NetworkType._3G]: 30000,
    [NetworkType._4G]: 0,
    [NetworkType.Unknown]: 0,
  };

  // Utility to determine file size adjustment
  const calculateFileSizeAdjustment = (
    file: Blob | null | undefined,
    isMobile: boolean,
  ): number => {
    if (!file) return 0;
    const profile = isMobile ? 'Mobile' : 'Desktop';
    const { perMB, max } = FileSizeAdjustments[profile];
    const fileSizeMB = file.size / (1024 * 1024);
    return Math.min(fileSizeMB * perMB, max);
  };

  // Utility to determine network adjustment
  const calculateNetworkAdjustment = (): number => {
    const { connection } = navigator as Navigator & { connection?: { effectiveType?: string } };
    if (!connection) return 0;
    const effectiveType = (connection.effectiveType as NetworkType) || NetworkType.Unknown;
    return NetworkAdjustments[effectiveType] ?? 0;
  };

  // Main timeout calculation
  const getAbortTimeoutDuration = ({ file, isMobile }: AbortTimeoutConfig): number => {
    return (
      BASE_ABORT_TIMEOUT +
      calculateFileSizeAdjustment(file, isMobile) +
      calculateNetworkAdjustment()
    );
  };

  const handleHistoryError = (error: AxiosError) => {
    const message = getErrorMessage(error);
    toast.error(message);
  };

  const sendMessage = async ({ file, text, onError }: TSendMessageProps) => {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const hasLargeFile = !!(file && file.size > 1024 * 1024 * 5); // 5MB

    const { mainTimeoutDuration, initialTimeoutDuration } = calculateTimeoutDurations(
      isMobile,
      hasLargeFile,
    );

    const timeoutId = setTimeout(async () => {
      toast.error('Response taking too long. Please try again.');

      try {
        await refreshChatHistory?.();
      } catch (error) {
        handleHistoryError(error);
        setInputsDisabled(false);
      }
    }, mainTimeoutDuration);

    let thisRequestReceivedResponse = false;
    let initialResponseTimeout: NodeJS.Timeout | null = null;

    if (!hasReceivedFirstResponse.current) {
      initialResponseTimeout = setTimeout(async () => {
        if (!thisRequestReceivedResponse && !hasReceivedFirstResponse.current) {
          toast.error('No response detected. Please try again.');
          try {
            await refreshChatHistory?.();
          } catch (error) {
            handleHistoryError(error);
            setInputsDisabled(false);
          }
        }
      }, initialTimeoutDuration);
    }

    const formdata = new FormData();
    if (file) {
      formdata.append('file', file);
    }
    formdata.append('text', text);

    try {
      const controller = new AbortController();

      // Calculate dynamic timeout based on file size and network conditions
      const timeoutDuration = getAbortTimeoutDuration({
        file,
        isMobile,
      });

      const abortTimeout = setTimeout(() => controller.abort(), timeoutDuration);

      const conversationId = useChatStore.getState().currentConversationId;
      const sse = await fetch(`${import.meta.env.VITE_CHAT_URL}/chat/${conversationId}`, {
        method: 'PUT',
        redirect: 'follow',
        body: formdata,
        signal: controller.signal,
      });

      if (sse.body && sse.body.getReader) {
        const reader = sse.body.getReader();

        const readChunk = async () => {
          try {
            const { done, value } = await reader.read();
            if (done) {
              reader.releaseLock();
              clearTimeout(timeoutId);

              if (initialResponseTimeout) {
                clearTimeout(initialResponseTimeout);
              }

              clearTimeout(abortTimeout);
              return;
            }

            const message = parse(value) as TResponseHandlerProps | undefined;
            if (message) {
              // Mark that we received first response
              thisRequestReceivedResponse = true;
              if (!hasReceivedFirstResponse.current) {
                hasReceivedFirstResponse.current = true;
                if (initialResponseTimeout) {
                  clearTimeout(initialResponseTimeout);
                }
              }
              messageHandler(message, timeoutId);
            }

            readChunk();
          } catch (readError) {
            console.error('Error reading from stream:', readError);
            reader.releaseLock();
            clearTimeout(abortTimeout);
            if (initialResponseTimeout) {
              clearTimeout(initialResponseTimeout);
            }
            toast.error('Connection interrupted. Please try again.');
            try {
              await refreshChatHistory?.();
            } catch (error) {
              handleHistoryError(error);
              setInputsDisabled(false);
            }
          }
        };

        readChunk();
      }
    } catch (error) {
      clearTimeout(timeoutId);
      if (initialResponseTimeout) {
        clearTimeout(initialResponseTimeout);
      }

      Sentry.captureException(error);
      onError(error as AxiosError);
    }
  };

  const onSendMessageErrorHandler = (error: unknown) => {
    const axiosError = error as AxiosError;
    let message = getErrorMessage(axiosError);

    // Check for TypeError: Failed to fetch
    if (error instanceof TypeError && error.message === ERROR_MESSAGES.FAILED_TO_FETCH) {
      message = ERROR_MESSAGES.FAILED_TO_SEND_MESSAGE_WHILE_NETWORK_ERROR;
    }

    toast.error(message);
    setInputsDisabled(false);
  };

  return { sendMessage, onSendMessageErrorHandler };
};

export default useSendMessage;
